import { faPlus, faSliders, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/design-system/primitives/Button';
import { ChipButton } from '@/design-system/primitives/ChipButton';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import { TextInput } from '@/design-system/primitives/TextInput';
import { useCustomExclusions } from '@/hooks/useCustomExclusions';
import type { AppCategory } from '@/types/itinerary';
import { appCategories } from '@/types/itinerary';
import { categoryDefinitions } from '@/utils/categories';
import { twx } from '@/utils/tailwind';

const radiusOptions = [5, 8, 10, 13] as const;

export interface FiltersPopoverProps {
  className?: string;
  availableCategories: Array<AppCategory>;
  enabledCategoryCount: number;
  onOpenCategoryCustomization: () => void;
}

export const FiltersPopover = ({
  className,
  availableCategories,
  enabledCategoryCount,
  onOpenCategoryCustomization,
}: FiltersPopoverProps) => {
  const [radius, setRadius] = useQueryState('radius', parseAsInteger.withDefault(10));
  const [selectedCategories, setSelectedCategories] = useQueryState(
    'categories',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const clampedRadius = radiusOptions.includes(radius as (typeof radiusOptions)[number])
    ? radius
    : 10;

  const customExclusions = useCustomExclusions();
  const [exclusionDraft, setExclusionDraft] = useState('');
  const handleAddExclusion = () => {
    const value = exclusionDraft.trim();
    if (!value) return;
    customExclusions.addNameContains(value);
    setExclusionDraft('');
  };

  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Small-screen positioning: anchor the popover to the viewport (fixed +
  // 8px gutter each side) rather than absolutely against the trigger's
  // wrapper — otherwise the right-anchored popover overflows the left
  // edge when the trigger sits near the right of the viewport.
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 479.98px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 479.98px)');
    const onChange = (e: MediaQueryListEvent) => setIsSmallScreen(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [smallScreenTop, setSmallScreenTop] = useState<number | null>(null);
  const measureSmallScreenTop = (): number | null => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return rect.bottom + 6;
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: measureSmallScreenTop is stable per-render and only reads ref
  useEffect(() => {
    if (!(isOpen && isSmallScreen)) return;
    let pending = false;
    const onResize = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        setSmallScreenTop(measureSmallScreenTop());
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, isSmallScreen]);

  const handleTriggerClick = () => {
    setIsOpen((wasOpen) => {
      const next = !wasOpen;
      if (next && isSmallScreen) setSmallScreenTop(measureSmallScreenTop());
      else setSmallScreenTop(null);
      return next;
    });
  };

  // Always show every category chip so the filter set reads as a fixed,
  // recognizable surface — categories not present in the current result
  // set (e.g. Explore mode before searching) still render so the user
  // can pre-select them.
  void availableCategories;
  const visibleCategories = appCategories.filter((c) => c !== 'other');
  const allActive = selectedCategories.length === 0;

  const handleAll = () => void setSelectedCategories(null);
  const handleToggleGroup = (category: AppCategory) => {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter((c) => c !== category)
      : [...selectedCategories, category];
    void setSelectedCategories(next.length > 0 ? next : null);
  };

  // Active categories count for the trigger badge — 0 means "All".
  const activeCategoryCount = selectedCategories.length;
  void allActive; // kept above for the in-popover "All" button logic

  const smallScreenStyle: CSSProperties | undefined =
    smallScreenTop !== null ? { top: smallScreenTop, left: 8, right: 8 } : undefined;

  // The popover body is shared between the small-screen portaled fixed
  // layout and the desktop absolute-positioned layout. Keeping them in
  // one place avoids drift between the two render paths.
  const popoverBody = (
    <>
      <div className="p-4 border-b border-content-divider">
        <SectionEyebrow className="mb-2">Search radius</SectionEyebrow>
        <div className="flex flex-wrap gap-1.5">
          {radiusOptions.map((miles) => (
            <ChipButton
              key={miles}
              selected={miles === clampedRadius}
              onClick={() => void setRadius(miles)}
              className="font-mono"
            >
              {miles}mi
            </ChipButton>
          ))}
        </div>
      </div>

      <div className="p-4 border-b border-content-divider">
        <SectionEyebrow className="mb-1">Category Display</SectionEyebrow>
        <p className="text-xs text-content-subtle mb-2.5">
          Choose which types of places appear in the list and on the map.
        </p>
        <div className="flex flex-wrap gap-1.5">
          <ChipButton selected={allActive} onClick={handleAll}>
            All
          </ChipButton>
          {visibleCategories.map((category) => {
            const def = categoryDefinitions[category];
            const isActive = selectedCategories.includes(category);
            return (
              <ChipButton
                key={category}
                icon={def.icon}
                selected={isActive}
                onClick={() => handleToggleGroup(category)}
              >
                {def.label}
              </ChipButton>
            );
          })}
        </div>
      </div>

      <div className="p-4 border-b border-content-divider">
        <SectionEyebrow className="mb-1">Custom Exclusions</SectionEyebrow>
        <p className="text-xs text-content-subtle mb-2.5">
          Hide places whose name contains any of these phrases (case-insensitive). Useful for
          filtering out spots like "Town Offices" or "Storage" that the places API surfaces but
          aren't useful for trip planning.
        </p>
        <div className="flex items-center gap-1.5 mb-2">
          <TextInput
            size="sm"
            value={exclusionDraft}
            onChange={(e) => setExclusionDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddExclusion();
              }
            }}
            placeholder="e.g. Town Offices"
            aria-label="New exclusion phrase"
            className="flex-1 min-w-0"
          />
          <button
            type="button"
            onClick={handleAddExclusion}
            disabled={exclusionDraft.trim().length === 0}
            aria-label="Add exclusion"
            title="Add exclusion"
            className={twx(
              'shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors',
              exclusionDraft.trim().length === 0
                ? 'border-content-divider text-content-disabled cursor-not-allowed'
                : 'border-secondary-main bg-secondary-subtle text-secondary-emphasized hover:bg-secondary-main hover:text-white',
            )}
          >
            <FontAwesomeIcon icon={faPlus} style={{ width: 11, height: 11 }} />
          </button>
        </div>
        {customExclusions.exclusions.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {customExclusions.exclusions.map((ex) => (
              <li key={ex.id}>
                <span
                  className={twx(
                    'inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full',
                    'text-xs font-medium bg-content-focal border border-content-border text-content-text',
                  )}
                >
                  <span className="truncate max-w-[12rem]">{ex.value}</span>
                  <button
                    type="button"
                    onClick={() => customExclusions.remove(ex.id)}
                    aria-label={`Remove exclusion "${ex.value}"`}
                    title="Remove"
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-content-subtle hover:bg-error-background hover:text-error-main transition-colors"
                  >
                    <FontAwesomeIcon icon={faXmark} style={{ width: 9, height: 9 }} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-content-disabled italic">No exclusions yet.</p>
        )}
      </div>

      <div className="p-4">
        <SectionEyebrow className="mb-1">Category Discovery</SectionEyebrow>
        <p className="text-xs text-content-subtle mb-2.5">
          Choose which types of places are fetched from the places API provider; these places are
          then displayed as part of the display categories above. It is reccomended to only select
          categories that are applicable to the trip you are planning.
          <strong>Enabling additional categories incurs additional API costs.</strong>
        </p>
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="font-medium text-content-text">{enabledCategoryCount}</span>{' '}
            <span className="text-content-subtle">categories enabled</span>
          </div>
          <Button
            size="sm"
            className="rounded-full"
            onClick={() => {
              setIsOpen(false);
              onOpenCategoryCustomization();
            }}
          >
            Customize…
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className={twx('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-label="Filters"
        title="Filters"
        className="relative flex items-center gap-1.5 h-9 px-2.5 rounded-full text-white/85 hover:text-white hover:bg-white/10 transition-colors"
      >
        <span className="text-xs font-mono font-medium">{clampedRadius}mi</span>
        <FontAwesomeIcon icon={faSliders} style={{ width: 14, height: 14 }} />
        {activeCategoryCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold leading-none bg-white text-secondary-emphasized inline-flex items-center justify-center shadow">
            {activeCategoryCount}
          </span>
        )}
      </button>

      {/* Small-screen popover: portaled to body + fixed position relative
          to the viewport, with 8px gutters left and right. Avoids
          ancestor stacking-context / containing-block traps. */}
      {isOpen &&
        isSmallScreen &&
        createPortal(
          <div
            ref={popoverRef}
            style={smallScreenStyle}
            className="fixed bg-white border border-content-border rounded-2xl shadow-xl z-menu overflow-hidden"
          >
            {popoverBody}
          </div>,
          document.body,
        )}

      {/* Desktop popover: anchored right against the trigger's wrapper. */}
      {isOpen && !isSmallScreen && (
        <div
          ref={popoverRef}
          className="absolute top-full right-0 mt-1.5 w-[min(36rem,calc(100vw-2rem))] bg-white border border-content-border rounded-2xl shadow-xl z-menu overflow-hidden"
        >
          {popoverBody}
        </div>
      )}
    </div>
  );
};
